import {Map} from 'immutable';
import {createUseItemBankStoreInstance} from '../FitbItemBank/store';

/**
 * This file consists of hooks and functions for utilizing the new zustand question stores into
 * the current question flow. The first portion regarding the storeRegistry, is a recreation of the pattern
 * utilized in the current mandark framework.js. Because zustand stores are not objects like the legacy stores
 * There is one workaround we made to make draft guesses compatible with the current way questions are used
 *
 * Previously stores were accessed directly via question.getStore(). This approach does not work with zustand and hooks
 * as those are more opinionated and have different restrictions on how state is managed.
 *
 * The work around I wrote allows us to have multiple instances of zustand stores that can be accessed, as opposed to
 * looping through questions and mapping the values from stores.
 *
 * As of now `useQuestionStore` is used for constructing props for the legacy assignment flows to work
 *
 * For example, an assignment gets all draft ids by looping through question sets and getting the values from question.getStore().
 * Hooks cannot be used in a class component, and not in the way the store objects were being utilized. The solution
 * was to create a draftId prop, that loops through all the question store hook instances, gets the ids and creates a prop
 * This does not violate hook rules, because this should only be done once the total question sets are fetched which allows for
 * the order and number of hooks to remain the same.
 *
 * Once the new question types are rendering correctly, we can move towards creating a new draft guess store, if the only reason
 * we have multiple instances of question stores is for draft info.
 */

// ------------ repeats what framework.js does for creating/storing stores ------------------

let storeRegistry = Map();

export const getRegisteredStores = () => {
  return storeRegistry;
};
// want to have a function for creating an instance of a useStoreState hook
// const storeHookCreator = (questionType: string) => create<QuestionStateT>(StateDefinition); // would get definition from a map

const questionTypes = ['fitb-item-bank'] as const;
//

const storeHookCreator = (type: string) => {
  if (type === questionTypes[0]) {
    // TODO: map question types to store instance.
    const store = createUseItemBankStoreInstance();
    return store;
  } else {
    const store = createUseItemBankStoreInstance();
    return store;
  }
};
// TODO use create type creator from stores
// (questionType: string) => create<QuestionStateT>(StateDefinition); // would get definition from a map
export type StoreInstanceType = ReturnType<typeof storeHookCreator>;

export function getStoreByName(name: string): StoreInstanceType | false {
  return storeRegistry.getIn([name, 'store'], false);
}

// naming is from framework.js this would be referring to zustand stores/hooks
export function setUpQuestionStore(questionType: string, name: string) {
  let storeInstance = getStoreByName(name);
  if (storeInstance === false) {
    storeInstance = storeHookCreator(questionType);
    storeRegistry = storeRegistry.set(
      name,
      Map({
        store: storeInstance
      })
    );
  }
  return storeInstance;
}

// ------------ End of framework.js replication  ------------------

/*
Functions for determining what type of store a given instances is. Mainly used for question render areas as
legacy stores and zustand stores have different ways of interacting with them.

example:

const storeName = isLegacyStore(store) ? activeQuestionStore.getName() : activeQuestion.getStoreName();

or

if(isLegacyStore(store)){
   callTargetedAction({...})
}

*/
const legacyStoreAttributes = ['getQuestionId', 'getQuestion', 'name', 'handlers'];

export const isLegacyStore = (store) => {
  return (
    typeof store === 'object' &&
    legacyStoreAttributes.every((attr) => Object.keys(store).includes(attr))
  );
};

export const hasZustand = (store) => {
  const containsZustand = store && !isLegacyStore(store);
  return containsZustand;
};
